import React from "react";
import { Link } from "react-router-dom";
import Layout from "../../layout/Layout";
import {
  Card,
  Button,
  Badge,
  Row,
  Col,
  Statistic,
  List,
  Typography,
  Space,
  Progress,
  Tag
} from "antd";
import {
  MonitorOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  BarChartOutlined,
  BranchesOutlined,
  FileTextOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  ArrowRightOutlined,
  DollarOutlined
} from "@ant-design/icons";

const { Title, Paragraph } = Typography;

const Index = () => {
  // Mock data for dashboard stats
  const stats = [
    {
      title: "Active Stations",
      value: 12,
      change: "+2 this week",
      trend: "up",
      icon: <SettingOutlined />,
      color: "#2563EB"
    },
    {
      title: "Online Devices",
      value: "8/10",
      change: "2 offline",
      trend: "warning",
      icon: <MonitorOutlined />,
      color: "#10B981"
    },
    {
      title: "Sync Success Rate",
      value: 98.5,
      suffix: "%",
      change: "+0.5% today",
      trend: "up",
      icon: <RiseOutlined />,
      color: "#059669"
    },
    {
      title: "Total Orders Today",
      value: 324,
      change: "+12% vs yesterday",
      trend: "up",
      icon: <BarChartOutlined />,
      color: "#EA580C"
    }
  ];

  const modules = [
    {
      title: "Station Management",
      description: "Configure output methods, assign devices, and manage kitchen stations",
      icon: <SettingOutlined />,
      href: "/sectionTable/stations",
      badge: "Essential",
      badgeColor: "blue",
      features: ["KDS/Printer Assignment", "Output Method Config", "Station Properties"]
    },
    {
      title: "KDS Device Registry",
      description: "Register, manage, and monitor physical KDS tablets and their connections",
      icon: <UsergroupAddOutlined />,
      href: "/sectionTable/devices",
      badge: "Core",
      badgeColor: "green",
      features: ["Device Registration", "Online/Offline Status", "Station Assignment"]
    },
    {
      title: "Bill Splits",
      description: "View and manage orders with split billing methods and sub-check details",
      icon: <FileTextOutlined />,
      href: "/sectionTable/bill-splits",
      badge: "Billing",
      badgeColor: "cyan",
      features: ["Split Order Tracking", "Sub-Check Management", "Payment Status"]
    },
    {
      title: "Routing Simulation",
      description: "Test and preview routing logic for menu items and order types",
      icon: <BranchesOutlined />,
      href: "/sectionTable/routing",
      badge: "Testing",
      badgeColor: "purple",
      features: ["Menu Item Testing", "Route Preview", "Error Detection"]
    },
    {
      title: "System Monitoring",
      description: "Monitor sync logs, device performance, and system health metrics",
      icon: <BarChartOutlined />,
      href: "/sectionTable/monitor",
      badge: "Analytics",
      badgeColor: "orange",
      features: ["Sync Logs", "Performance Metrics", "Alert Management"]
    }
  ];

  const recentAlerts = [
    {
      type: "warning",
      message: "Grill Station KDS #2 has been offline for 5 minutes",
      time: "2 min ago",
      action: "Check Device",
      icon: <WarningOutlined />
    },
    {
      type: "success",
      message: "Cold Station successfully reassigned to KDS #4",
      time: "15 min ago",
      action: "View Details",
      icon: <CheckCircleOutlined />
    },
    {
      type: "info",
      message: "3 new orders routed to Expeditor Station",
      time: "23 min ago",
      action: "Monitor",
      icon: <ClockCircleOutlined />
    }
  ];

  const getAlertColor = (type) => {
    switch (type) {
      case 'warning': return '#FA8C16';
      case 'success': return '#52C41A';
      case 'info': return '#1890FF';
      default: return '#666';
    }
  };

  return (
    <Layout>
      <div style={{ padding: "0 24px" }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <Title level={1}>KDS Management Dashboard</Title>
          <Paragraph style={{ fontSize: "16px", color: "#666" }}>
            Manage kitchen display systems, stations, and routing across your restaurant operations
          </Paragraph>
        </div>

        {/* Stats Grid */}
        <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
          {stats.map((stat, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <Statistic
                      title={stat.title}
                      value={stat.value}
                      suffix={stat.suffix}
                      valueStyle={{ 
                        color: stat.color,
                        fontSize: "28px",
                        fontWeight: "bold"
                      }}
                    />
                    <div style={{ 
                      marginTop: "8px",
                      color: stat.trend === 'up' ? '#52c41a' : 
                             stat.trend === 'warning' ? '#fa8c16' : '#666',
                      fontSize: "12px"
                    }}>
                      {stat.change}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: "24px", 
                    color: stat.color,
                    opacity: 0.8 
                  }}>
                    {stat.icon}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Main Modules Grid */}
        <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
          {modules.map((module, index) => (
            <Col xs={24} lg={12} key={index}>
              <Card 
                hoverable
                style={{ height: "100%" }}
                actions={[
                  <Link to={module.href} key="access">
                    <Button type="primary" block>
                      Access {module.title}
                      <ArrowRightOutlined />
                    </Button>
                  </Link>
                ]}
              >
                <Card.Meta
                  avatar={
                    <div style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#f0f0f0",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "24px",
                      color: "#666"
                    }}>
                      {module.icon}
                    </div>
                  }
                  title={
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>{module.title}</span>
                      <Badge color={module.badgeColor} text={module.badge} />
                    </div>
                  }
                  description={module.description}
                />
                <div style={{ marginTop: "16px" }}>
                  <List
                    size="small"
                    dataSource={module.features}
                    renderItem={(feature) => (
                      <List.Item style={{ padding: "4px 0" }}>
                        <Space>
                          <CheckCircleOutlined style={{ color: "#52c41a" }} />
                          <span style={{ fontSize: "14px" }}>{feature}</span>
                        </Space>
                      </List.Item>
                    )}
                  />
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Recent Activity & Quick Actions */}
        <Row gutter={[24, 24]}>
          {/* Recent Alerts */}
          <Col xs={24} lg={16}>
            <Card 
              title={
                <Space>
                  <WarningOutlined style={{ color: "#fa8c16" }} />
                  Recent Alerts & Activity
                </Space>
              }
            >
              <List
                dataSource={recentAlerts}
                renderItem={(alert) => (
                  <List.Item
                    actions={[
                      <Button size="small" key="action">
                        {alert.action}
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <div style={{ color: getAlertColor(alert.type) }}>
                          {alert.icon}
                        </div>
                      }
                      title={alert.message}
                      description={alert.time}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Quick Actions */}
          <Col xs={24} lg={8}>
            <Card title="Quick Actions">
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Link to="/stations">
                  <Button block>
                    <SettingOutlined />
                    Add New Station
                  </Button>
                </Link>
                <Link to="/devices">
                  <Button block>
                    <UsergroupAddOutlined />
                    Register KDS Device
                  </Button>
                </Link>
                <Link to="/bill-splits">
                  <Button block>
                    <DollarOutlined />
                    View Bill Splits
                  </Button>
                </Link>
                <Link to="/routing">
                  <Button block>
                    <BranchesOutlined />
                    Test Routing
                  </Button>
                </Link>
                <Link to="/logs">
                  <Button block>
                    <FileTextOutlined />
                    View Sync Logs
                  </Button>
                </Link>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default Index;
